import { useEffect } from 'react';

const BACKDROP_SELECTOR = '#CookiebotWidget-backdrop, .CookiebotWidget-modal-backdrop';
const POINTER_EVENTS = ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click', 'touchstart', 'touchend', 'touchcancel', 'touchmove', 'wheel'];

const canScrollPanel = (target, panel, deltaY) => {
  if (!deltaY) return false;
  for (let element = target; element && panel.contains(element); element = element.parentElement) {
    if (!/^(auto|scroll)$/.test(getComputedStyle(element).overflowY)) continue;
    if (deltaY < 0 && element.scrollTop > 0) return true;
    if (deltaY > 0 && element.scrollTop + element.clientHeight < element.scrollHeight - 1) return true;
  }
  return false;
};

const lockPageScroll = () => {
  const root = document.documentElement;
  const body = document.body;
  const { scrollX, scrollY } = window;
  const changes = [
    [root, 'overflow', 'hidden'],
    [root, 'overscroll-behavior', 'none'],
    [body, 'position', 'fixed'],
    [body, 'top', `${-scrollY}px`],
    [body, 'left', `${-scrollX}px`],
    [body, 'width', '100%'],
    [body, 'overflow', 'hidden'],
    [body, 'overscroll-behavior', 'none'],
  ];
  const previous = changes.map(([element, property]) => [
    element, property, element.style.getPropertyValue(property), element.style.getPropertyPriority(property),
  ]);
  const enforce = () => changes.forEach(([element, property, value]) => {
    if (element.style.getPropertyValue(property) !== value || element.style.getPropertyPriority(property) !== 'important') {
      element.style.setProperty(property, value, 'important');
    }
  });
  enforce();

  const restore = () => {
    previous.forEach(([element, property, value, priority]) => {
      if (value) element.style.setProperty(property, value, priority);
      else element.style.removeProperty(property);
    });
    // The booking page uses smooth scrolling; restore its position immediately.
    const behavior = root.style.getPropertyValue('scroll-behavior');
    const priority = root.style.getPropertyPriority('scroll-behavior');
    root.style.setProperty('scroll-behavior', 'auto', 'important');
    window.scrollTo(scrollX, scrollY);
    if (behavior) root.style.setProperty('scroll-behavior', behavior, priority);
    else root.style.removeProperty('scroll-behavior');
  };
  return { enforce, restore };
};

export const observeCookiebotMobileBackdrop = () => {
  // Include landscape phones, tablets, and touch devices with a wider viewport.
  const mobile = window.matchMedia('(max-width: 991px), (any-pointer: coarse)');
  let scrollLock;
  let touchY;
  const syncScrollLock = () => {
    const shouldLock = mobile.matches && !!document.querySelector('#CookiebotWidget.CookiebotWidget-open');
    if (shouldLock) {
      if (!scrollLock) scrollLock = lockPageScroll();
      else scrollLock.enforce();
    } else if (scrollLock) {
      scrollLock.restore();
      scrollLock = undefined;
      touchY = undefined;
    }
  };
  // Cookiebot can load late and update page styles after opening its panel.
  const observer = new MutationObserver(syncScrollLock);
  observer.observe(document.documentElement, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'],
  });
  mobile.addEventListener?.('change', syncScrollLock);
  syncScrollLock();

  const blockBackground = (event) => {
    syncScrollLock();
    if (!mobile.matches) return;
    const widget = document.querySelector('#CookiebotWidget.CookiebotWidget-open');
    if (!widget) return;

    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target) return;
    // "Change your consent" can open Cookiebot's main dialog outside the widget.
    const consentDialog = target.closest('#CybotCookiebotDialog');
    let block = !consentDialog && (!widget.contains(target) || target === widget ||
      target.id === 'CookiebotWidget-widgetWrapper' || target.closest(BACKDROP_SELECTOR));
    const panel = consentDialog || widget;
    if (event.type === 'touchstart') touchY = event.touches?.[0]?.clientY;
    if (event.type === 'touchend' || event.type === 'touchcancel') touchY = undefined;
    if (event.type === 'touchmove') {
      const nextY = event.touches?.[0]?.clientY;
      const deltaY = touchY === undefined || nextY === undefined ? 0 : touchY - nextY;
      touchY = nextY;
      if (event.touches?.length === 1) block ||= !canScrollPanel(target, panel, deltaY);
      else if (!event.touches?.length) block = true;
    }
    if (event.type === 'wheel') block ||= !canScrollPanel(target, panel, event.deltaY);
    if (!block) return;

    if (event.cancelable) event.preventDefault();
    event.stopImmediatePropagation();
  };

  // Capture before the vendor's document/backdrop handlers so background taps
  // neither dismiss the widget nor reach the booking page underneath it.
  const options = { capture: true, passive: false };
  POINTER_EVENTS.forEach(type => window.addEventListener(type, blockBackground, options));
  return () => {
    observer.disconnect();
    mobile.removeEventListener?.('change', syncScrollLock);
    POINTER_EVENTS.forEach(type => window.removeEventListener(type, blockBackground, true));
    scrollLock?.restore();
    scrollLock = undefined;
  };
};

export const useCookiebotMobileBackdrop = () => useEffect(observeCookiebotMobileBackdrop, []);
